import { memone } from '@deqo/foundation';
import { getStroke } from 'perfect-freehand';
import { Component, component, property } from '../../../concepts/objects';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { Matrix } from '../../../platform/geom/Matrix';
import { Point } from '../../../platform/geom/Point';
import { Polyline } from '../../../platform/geom/Polyline';
import { Physical } from '../../canvas/entities/Physical';
import { Graffiti } from '../../canvas/visualization/Graffiti';

const reactionDef = {
    name: 'Marking',
    implements: [Graffiti],
};

@component(reactionDef)
export class Marking extends Component implements Graffiti {

    public static formatValue(points: Point[]) {
        return points.flatMap(p => [p.x, p.y]);
    }

    public static toStroke(rawData: number[], size: number) {

        const pairs = [] as [number, number][];
        for (let i = 1; i < rawData.length; i += 2) {
            pairs.push([rawData[i - 1], rawData[i]]);
        }

        return getStroke(pairs, {
            size,
            smoothing: 0.6,
            thinning: 0.15,
            streamline: 0.6,
            easing: (t) => t,
            start: {
                taper: 0,
                cap: true,
            },
            end: {
                taper: 0,
                cap: true,
            },
        });
    }

    public static toStrokePath(rawData: number[], size: number) {

        if (rawData.length === 4) {
            const path = `M ${rawData[0]} ${rawData[1]} L ${rawData[2]} ${rawData[3]}`;
            return { path, style: 'stroke', size };
        }

        const stroke = Marking.toStroke(rawData, size);
        if (!stroke.length) return null;

        const d = stroke.reduce(
            (acc, [x0, y0], i, arr) => {
                const [x1, y1] = arr[(i + 1) % arr.length];
                acc.push(x0, y0, (x0 + x1) / 2, (y0 + y1) / 2);
                return acc;
            },
            ['M', ...stroke[0], 'Q']
        );

        d.push('Z');
        return { path: d.join(' '), style: 'fill', size };
    }

    @property({ type: 'string', default: '#000' })
    public color!: string;

    @property({ type: 'number', default: 5 })
    public size!: number;

    @property({ type: 'string' })
    public value!: string;

    @property({ type: 'string', default: 'topLeft' })
    public origin!: 'topLeft' | 'center';

    public outline() {
        const ol = this.parseDataIntoOutline(this.value, this.size);
        const oo = this.originOffset();
        return ol.transform(Matrix.translate(oo.x, oo.y));
    }

    public paintTo(gfx: UniversalSurfaceRenderer): void {

        const { path, size, style } = this.parseDataIntoPath(this.value, this.size) ?? {};
        if (!path) return;

        const oo = this.originOffset();
        gfx.translate(oo.x, oo.y);

        if (style === 'fill') {
            gfx.fillStyle = this.color;
            gfx.fill(new Path2D(path));
        }
        else {
            gfx.strokeStyle = this.color;
            gfx.lineWidth = size;
            gfx.lineCap = 'round';
            gfx.stroke(new Path2D(path));
        }
    }

    public toPoints() {
        const data = JSON.parse(this.value) as number[];
        const points = [] as Point[];
        for (let i = 0; i < data.length; i += 2) {
            points.push(new Point(data[i], data[i + 1]));
        }
        return points;
    }

    private originOffset() {
        if (this.origin === 'center') {
            const ps = this.parent?.as(Physical)?.size();
            if (ps) {
                return new Point(ps.w / 2, ps.h / 2);
            }
        }
        return Point.zero;
    }

    @memone()
    private parseDataIntoOutline(value: string, size: number) {
        const stroke = Marking.toStroke(JSON.parse(value) as number[], size);
        return Polyline.from(stroke.map(xy => Point.from(xy)), true);
    }

    @memone()
    private parseDataIntoPath(value: string, size: number) {
        return Marking.toStrokePath(JSON.parse(value) as number[], size);
    }
}
